import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type Item } from '../lib/api.ts';

/**
 * Item imagery, for match inventories.
 *
 * Matches store bare item ids, so a lookup is needed to render anything but a
 * number. Loaded once for the app and served from Valve's CDN, exactly as hero
 * icons are — the set is ~400 entries and changes only when Valve ships an item.
 */

const ITEM_IMAGE_BASE = 'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/dota_react/items';

interface ItemLookup {
  name: (itemId: number) => string;
  slug: (itemId: number) => string | null;
}

const ItemContext = createContext<ItemLookup | null>(null);

export function ItemProvider({ children }: { children: ReactNode }) {
  const { data } = useQuery({
    queryKey: ['items'],
    queryFn: api.items,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
  });

  const value = useMemo<ItemLookup>(() => {
    const byId = new Map<number, Item>((data?.items ?? []).map((i) => [i.id, i]));
    return {
      name: (itemId) => byId.get(itemId)?.name ?? `Item ${itemId}`,
      slug: (itemId) => byId.get(itemId)?.slug ?? null,
    };
  }, [data]);

  return <ItemContext.Provider value={value}>{children}</ItemContext.Provider>;
}

export function useItems(): ItemLookup {
  const context = useContext(ItemContext);
  if (context) return context;
  // Usable outside the provider without throwing, as HeroIcon is.
  return { name: (id) => `Item ${id}`, slug: () => null };
}

/**
 * A six-slot inventory.
 *
 * Empty slots are drawn rather than collapsed, so every row on a scoreboard is
 * the same width and the eye can compare inventories down a column.
 */
export function ItemRow({ items }: { items: readonly number[] | null }) {
  const lookup = useItems();
  const slots = [...(items ?? [])].slice(0, 6);
  while (slots.length < 6) slots.push(0);

  return (
    <div className="flex gap-0.5">
      {slots.map((itemId, index) => {
        const slug = itemId > 0 ? lookup.slug(itemId) : null;
        return (
          <span
            key={index}
            title={itemId > 0 ? lookup.name(itemId) : undefined}
            className="block h-[22px] w-[30px] shrink-0 overflow-hidden rounded-[2px] bg-surface-3"
          >
            {slug && (
              <img
                src={`${ITEM_IMAGE_BASE}/${slug}.png`}
                alt={lookup.name(itemId)}
                width={30}
                height={22}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            )}
          </span>
        );
      })}
    </div>
  );
}
